import { InquiryValues } from '~/components/common/inquiry/drawer/types';
import {
  CurrentState,
  createNoteRequest,
  createUpdateInquiryRequest,
} from '~/components/common/inquiry/drawer/util';
import { useUpdateInquiryMutation } from '~/store/services/inquiry';
import { useCreateNoteMutation } from '~/store/services/notes/notes';
import { editorV2ContentHasText } from '~/lib/util/editorV2ContentHasText';

export type UpdateInquiryState = {
  execute: (
    inquiryId: string,
    currentUserId: string,
    pathname: string,
    patientId: string,
    updates: InquiryValues,
    state?: CurrentState,
  ) => Promise<any>;
  isUpdating: boolean;
};

export const useUpdateInquiry = (): UpdateInquiryState => {
  const [triggerUpdateInquiry, { isLoading: isInquiryUpdating }] =
    useUpdateInquiryMutation();
  const [triggerCreateNote, { isLoading: isNoteCreating }] =
    useCreateNoteMutation();

  const isUpdating = isNoteCreating || isInquiryUpdating;

  const execute = async (
    inquiryId: string,
    currentUserId: string,
    pathname: string,
    patientId: string,
    updates: InquiryValues,
    state?: CurrentState,
  ) => {
    const {
      note,
      patient,
      coverages: coverage,
      referral,
      contacts,
      info,
    } = updates;

    const noteRequest = editorV2ContentHasText(note.content)
      ? createNoteRequest(inquiryId, currentUserId, updates.patient.id, note)
      : null;

    const triggerUpdateInquiryPromise = triggerUpdateInquiry({
      body: createUpdateInquiryRequest(
        { contacts, coverage, info, patient, referral },
        state,
      ),
      inquiryId,
      pathname,
      patientId,
    });

    if (noteRequest) {
      return Promise.all([
        triggerUpdateInquiryPromise.unwrap(),
        triggerCreateNote(noteRequest).unwrap(),
      ]);
    } else {
      return triggerUpdateInquiryPromise.unwrap();
    }
  };

  return {
    execute,
    isUpdating,
  };
};
